import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { parseISO } from 'date-fns'
import { useSyncedState } from '~/state/useSyncedState'
import { useFilterDimsState } from '~/state/useFilterDimsState'
import { useGroupByState } from '~/state/useGroupByState'
import { useDimDefaults } from '~/state/useDimDefaults'
import { useSetMultipleSearchParams } from '~/state/useSetMultipleSearchParams'
import {
  useKpisForPage,
  usePageCharts,
  usePageControls,
  useRatioComponentFacts,
} from '~/api/hooks/useCharts'
import { useKpiCascadeState, type KpiCascadeState } from '~/state/useKpiCascadeState'
import { useGranularityState, type GranularityState } from '~/state/useGranularityState'
import { usePanelBaseState, type PanelBaseMode } from '~/state/usePanelBaseState'
import {
  resolveGovernance,
  countBuckets,
  shiftBackByBuckets,
  toWireBucket,
  type GranularityGovernance,
} from './granularityGovernance'
import { useTimeframePresetState, type TimeframePresetState } from '~/state/useTimeframePresetState'
import { resolveDefaultAnchor } from '~/state/defaultTimeframeAnchor'
import { useMultiTimeframeState, type MultiTimeframeState } from '~/state/useMultiTimeframeState'
import { PAGE_TIMEFRAME_DEFAULTS } from './pageDefaults'
import type { Facet } from '~/state/useChartKnobsState'
import { useDimCatalog } from '~/api/hooks/useCatalog'
import { pruneDimsToFact } from './pruneDimsToFact'
import { useCampaignState, type ActiveCampaign } from '~/state/useCampaignState'
import {
  campaignConsistent,
  mergeArmFilters,
  CAMPAIGN_MANAGED_DIMS,
} from '~/state/campaignPredicate'
import { labelForRootFromCache } from './kpiLabel'
import { isPinKnobKey } from '~/state/routeStateContract'

export type Archetype = 'status' | 'performance'

export interface ReviewPageStateInput {
  pageId: string
  factTable: string
  defaultKpi: string
  archetype: Archetype
  multiTimeframe?: boolean
  /** Page-seeded decomposition (the Contribution page): groupBy seed + stacked facet.
   *  Seeds yield to explicit URL state (useGroupByState contract). */
  decomp?: { groupByDefault: string; facet: Facet }
}

export interface WalkBaseParams {
  fact_table: string
  kpi: string
  from: string
  to: string
  filter_dims: Record<string, string[]>
  group_by: string[]
  bucket_size: 'day' | 'week' | 'month' | 'quarter' | 'year'
}

export interface ReviewPageState {
  pageId: string
  factTable: string
  archetype: Archetype
  fromIso: string
  toIso: string
  kpi: string
  setKpi: (kpi: string) => void
  filters: Record<string, string[]>
  groupBy: string[]
  decompFacet: Facet | null
  /** The page's seeded group-by dim ('' when none) — thread into DimFilterPanel's
   *  useGroupByState call so the control reflects the seed (two call sites, one default). */
  groupByDefault: string
  availableKpis: string[]
  baseParams: WalkBaseParams
  setMultipleSearchParams: (
    params: Record<string, string | null>,
    opts?: { replace?: boolean },
  ) => void
  kpiCascade: KpiCascadeState
  granularity: GranularityState
  governance: GranularityGovernance
  /** Row 15-light: raw URL token of the Vorperioden-Basis checkbox ('off' | 'prev'). */
  panelBase: PanelBaseMode
  setPanelBase: (m: PanelBaseMode) => void
  /** Inclusive bucket count of the effective window at the GOVERNED unit. */
  windowBuckets: number
  /** Row 15-light: non-null when panel_base=prev is APPLICABLE (≥2 panels, no multi-TF).
   *  Only the sub-period TIMELINE consumers extend their own walk `from` to this;
   *  `baseParams` stays the UNEXTENDED window (window integrity). Param-present-but-
   *  inapplicable is silently inert (row-20 precedent: gran slider inert in multi-TF). */
  panelBaseline: { baselineFromIso: string } | null
  timeframePreset: TimeframePresetState
  timeframeReady: boolean
  multiTimeframe: MultiTimeframeState | null
  /** Dim names dropped from the URL on prune (shown as a dismissible notice); null when clean. */
  prunedDims: string[] | null
  dismissPrunedNotice: () => void
  /** FU-31: display label of a cross-page-carried kpi_root the page couldn't resolve and reset to
   *  the page default (dismissible notice); null when clean. Resolved off cached get_controls. */
  removedKpi: string | null
  dismissRemovedKpiNotice: () => void
  /** True when the dim catalog query errored — scaffold shows an error, not a spinner. */
  dimCatalogError: boolean
  /** Campaigns C1 (spec §4): the reconciled active campaign, or null when no consistent campaign
   *  identity holds. Drives the id-based timeframe label + the campaign-aware prune notice. */
  campaign: ActiveCampaign | null
  /** Campaigns C3b (spec D-C3b-1): the page KPI's ratio component-fact pair when it is a walk_ratio
   *  KPI (resolved off the registry row's ratio_kpi), else null. Threaded so the per-arm resolver
   *  (useMultiTfTimeframes) applies the both-sides-or-nothing rule off the SAME pair as the main
   *  window, without re-deriving the registry lookup. Optional so the many hand-rolled test stubs
   *  stay valid; the hook always populates it (null on non-ratio pages). */
  ratioComponents?: { numFact: string; denomFact: string } | null
  /** Campaigns C3b (M1, final review): true when a campaign is active on a RATIO page AND a
   *  component-fact controls query settled in ERROR. Without this the readiness gate hangs false
   *  forever (the exact-vs-attribution decision can't be made) while the page-fact controls succeed,
   *  so the scaffold showed the misleading "no date range" warning. The scaffold routes this to its
   *  loud controls-error affordance instead. Optional so the many hand-rolled test stubs stay valid. */
  ratioComponentControlsError?: boolean
  /** FU-02(b): the ratio component-fact MAP is still IN FLIGHT on a ratio-family registry row — a
   *  multi-TF arm naming a campaign must HOLD rather than resolve one-sided. isPending, NOT
   *  data-absence: a settled map ERROR releases the hold to the single-fact fallback (visible
   *  degradation; FU-01 owns the loudness). Threaded into useMultiTfTimeframes' `pending`. */
  ratioMapPending: boolean
}

export function useReviewPageState({
  pageId,
  factTable,
  defaultKpi,
  archetype,
  multiTimeframe = false,
  decomp,
}: ReviewPageStateInput): ReviewPageState {
  const [fromIso] = useSyncedState('from', z.string(), 'url', '2024-01-01')
  const [toIso] = useSyncedState('to', z.string(), 'url', '2024-12-31')
  const [filters] = useFilterDimsState()
  // Campaigns C1: the RAW campaign param distinguishes "set-but-unresolved" (id present, controls
  // still loading, or a stale id) from "cleared". Reconciliation reads it below.
  const [campaignParam] = useSyncedState('campaign', z.string(), 'url', '')
  const [groupBy] = useGroupByState(decomp?.groupByDefault ?? '')
  const setMultipleSearchParams = useSetMultipleSearchParams()
  // FU-31: resolves the removed-KPI notice label off cached get_controls entries (other pages
  // the session visited) — the current page can't name a root it doesn't register.
  const qc = useQueryClient()

  const kpiCascade = useKpiCascadeState(pageId, factTable, defaultKpi)
  const multiTimeframeState = useMultiTimeframeState()

  const kpisQuery = useKpisForPage(pageId)
  const availableKpis =
    kpisQuery.isSuccess && kpisQuery.data.length > 0 ? kpisQuery.data : [defaultKpi]

  const selectedKpi = kpiCascade.encodedKpi || defaultKpi
  const chartsForKpi = usePageCharts(pageId, selectedKpi)
  const registryRow = chartsForKpi.data?.[0]
  const registryFact = registryRow?.default_params?.['fact_table']
  const activeFactTable = typeof registryFact === 'string' ? registryFact : factTable

  // Campaigns C3b (spec D-C3b-1): a ratio KPI resolves its campaign filters off the PAIR of
  // component facts, not the single registry fact. Look the pair up by the registry row's own kpi id
  // (the resolved ratio_kpi, which may differ from the bare selectedKpi). `ratioComponents` stays
  // null until the static component-facts map settles — useCampaignState then uses the single-fact
  // path transiently, and the timeframeReady gate below holds the walk until the pair (and both its
  // controls) resolve so no one-sided walk ever fires.
  //
  // C1 (final whole-branch review): ratio-family is decided by MEMBERSHIP in the component-facts map
  // keyed on the registry row's kpi — NOT by `walk_function === 'walk_ratio'`. That string check
  // missed CampaignPerformance's only enabled ratio slot (mig 0070 leaves each ratio KPI a single
  // `metric_driver` row whose walk_function is `walk_ratio_absolute`), so the whole page-state fix
  // never engaged there and CpO × campaign kept firing the one-sided exact form (D0 unfixed). The map
  // holds a row for every ratio KPI on every ratio walk family, so membership covers them uniformly.
  const ratioComponentFacts = useRatioComponentFacts()
  const registryKpi = registryRow?.kpi
  const ratioComponents =
    typeof registryKpi === 'string' ? (ratioComponentFacts.data?.[registryKpi] ?? null) : null
  // A resolved pair IS the ratio verdict (the map only holds ratio KPIs). Null while the map is
  // still pending — which coincides with the single-fact transient path below; the readiness gate
  // separately holds the walk (see `mightBeRatioPendingMap`) so a maybe-ratio page never fires early.
  const isRatioKpi = ratioComponents !== null
  // Component-fact controls, read here for the readiness gate (React Query dedupes these with the
  // identical reads inside useCampaignState — same queryKey, one network call). Null-gated.
  const numComponentControls = usePageControls(ratioComponents?.numFact ?? null)
  const denomComponentControls = usePageControls(ratioComponents?.denomFact ?? null)
  // The prune must run against the REGISTRY-resolved fact, not the static `factTable` fallback —
  // they differ on pages like ReviewKpis (static media_national vs registry kpi_overall). Acting
  // before usePageCharts settles would wrongly strip dims valid on the real fact (a load race).
  const registrySettled = chartsForKpi.data !== undefined

  // ── Dim catalog: prune carried dims before any walk fires ─────────────────
  // The catalog is cached (staleTime: Infinity) → a blink only on the first load of a session;
  // later navigation is instant. `dimsReady` gates chart rendering and controls `effectiveFilters`
  // / `effectiveGroupBy` so no walk fires with an un-pruned dim (the load-bearing invariant).
  const dimCatalog = useDimCatalog()
  const dimsReady = dimCatalog.isSuccess
  const pruneReady = dimsReady && registrySettled
  const [searchParams] = useSearchParams()
  const rawColumns = searchParams.get('columns')
  const factDims = useMemo(
    () =>
      new Set(
        (dimCatalog.data ?? [])
          .filter((r) => r.fact_table === activeFactTable)
          .map((r) => r.dim_name),
      ),
    [dimCatalog.data, activeFactTable],
  )
  const pruned = useMemo(
    () => pruneDimsToFact(filters, groupBy, rawColumns, factDims),
    [filters, groupBy, rawColumns, factDims],
  )
  // Effective dims drive every tile/walk; apply the prune only once both the catalog AND registry
  // are settled — pruneReady guards against wrongly acting on the transient static fallback fact.
  const effectiveFilters = pruneReady ? pruned.filters : filters
  const effectiveGroupBy = pruneReady ? pruned.groupBy : groupBy

  // Data-anchored default window (triage #5 / WP-B): the default timeframe opens on the active
  // fact's latest DATA date, capped at today — min(max_date, today). The locked rule and the
  // multi-fact rationale (primary/active fact, NOT max-across-facts) live in resolveDefaultAnchor.
  // The picker bounds are untouched — users can still select forward dates.
  const controls = usePageControls(activeFactTable)
  // Campaigns C1 (spec §4): resolve the campaign param against the active fact's controls. Null
  // while unset/unresolved/stale. The prune exemption below keeps managed dims in the URL so the
  // reconciliation always compares against the full predicate.
  const campaign = useCampaignState(activeFactTable, ratioComponents)
  const maxDataDate = controls.data?.min_max_dates?.max_date ?? null
  const todayUtc = parseISO(new Date().toISOString().slice(0, 10))
  const anchorDate = resolveDefaultAnchor(maxDataDate, todayUtc)
  const timeframePreset = useTimeframePresetState(
    anchorDate,
    PAGE_TIMEFRAME_DEFAULTS[pageId as keyof typeof PAGE_TIMEFRAME_DEFAULTS] ?? 'last_12_weeks',
  )
  // Charts must not render — so no walk fires — until both the dim catalog AND the registry fact
  // are settled. On chartsForKpi error we still unblock (don't hang forever); the chart tiles
  // themselves will surface the error. Catalog is cached (staleTime: Infinity) → a blink only on
  // the first load of a session; later navigation is instant.
  //
  // Campaigns C3 fix-wave #4: a cold-load `?campaign=<id>` URL must not fire a walk BEFORE the
  // campaign resolves — campaign resolution needs controls.data (useCampaignState reads
  // controls.data.campaign_timeslots), which the anchor/registry/catalog gates above don't wait
  // on (tf_preset=custom, set atomically with the campaign, already satisfies the first clause).
  // Without this, a hard-load fires an UNFILTERED walk, then re-fires once get_controls settles —
  // a flash of unfiltered totals plus a wasted walk. Absent/empty campaign param never gates (the
  // common case stays exactly as fast as before); controls.isError also leaves controls.data
  // undefined, so an errored get_controls query keeps timeframeReady false too — this does not
  // silently hang as a spinner: the scaffold's own controls.isError branch (ReviewPageScaffold.tsx)
  // renders the loud error Alert whenever !timeframeReady, ahead of its loading-spinner fallback.
  //
  // Campaigns C3b (D0 fix): for a RATIO KPI under a campaign the campaign resolves off the PAIR of
  // component facts (both-sides-or-nothing). Until the component-facts map AND both component
  // controls resolve, useCampaignState resolves off the single registry fact — a one-sided
  // /attribution form — so readiness must additionally wait on those, or a walk fires with the
  // wrong campaign scope then re-fires once the pair settles (the ratio analogue of fix-wave #4).
  // Non-ratio KPIs and the no-campaign case are unaffected (the clause short-circuits).
  // C1: with membership-based detection, `isRatioKpi` is false while the component-facts map is
  // still pending — so a page that MIGHT be a ratio must not be treated as ready-to-fire yet. Hold
  // readiness whenever the map is pending AND the registry walk_function is a ratio family
  // (`walk_ratio` / `walk_ratio_absolute`); a non-ratio walk_function never waits (the common path
  // stays exactly as fast as before). Once the map settles, membership is authoritative: a KPI in
  // the map gates on both component controls; a KPI absent from it is simply non-ratio, so it never
  // hangs — this dissolves the ledger's M2 (a ratio walk missing from the map) by construction.
  const ratioMapSettled = ratioComponentFacts.data !== undefined
  const registryWalkFn = registryRow?.walk_function
  const mightBeRatioPendingMap =
    !ratioMapSettled &&
    typeof registryWalkFn === 'string' &&
    registryWalkFn.startsWith('walk_ratio')
  // FU-02(b): the map is still IN FLIGHT and this page's registry row is ratio-family — a
  // multi-TF arm naming a campaign must hold (not resolve one-sided). isPending, NOT
  // data-absence: a settled map ERROR releases the hold (the arm falls back to the single-fact
  // path — visible degradation; FU-01's flag owns the page-level loudness for that error).
  const ratioMapPending =
    ratioComponentFacts.isPending &&
    typeof registryWalkFn === 'string' &&
    registryWalkFn.startsWith('walk_ratio')
  const ratioCampaignReady = isRatioKpi
    ? numComponentControls.data !== undefined && denomComponentControls.data !== undefined
    : !mightBeRatioPendingMap
  // M1 (final review): a SETTLED-in-error component-fact controls query leaves ratioCampaignReady
  // false forever (data stays undefined post-error), so the campaign readiness clause never lifts.
  // Surface it as a loud error instead of a permanent misleading "no date range" warning — the
  // scaffold routes this flag to its controls-error affordance. Only meaningful under an active
  // campaign on a ratio page (isRatioKpi ⇒ the component-controls queries are enabled).
  // FU-01 (map twin of M1): the component-facts MAP query settling in ERROR leaves data
  // undefined → isRatioKpi false, mightBeRatioPendingMap true forever → the same permanent
  // misleading no-date-range hold, with NO flag. The map term must NOT be gated on isRatioKpi
  // (it derives from the failed map); mightBeRatioPendingMap already scopes it to ratio-family
  // registry rows, so non-ratio pages never see the flag.
  const ratioComponentControlsError = Boolean(
    campaignParam !== '' &&
    ((isRatioKpi && (numComponentControls.isError || denomComponentControls.isError)) ||
      (mightBeRatioPendingMap && ratioComponentFacts.isError)),
  )
  const timeframeReady =
    (timeframePreset.isCustom || anchorDate !== undefined) &&
    dimsReady &&
    (registrySettled || chartsForKpi.isError) &&
    (campaignParam === '' || (controls.data !== undefined && ratioCampaignReady))

  const effectiveFrom = timeframePreset.computedRange?.fromIso ?? fromIso
  const effectiveTo = timeframePreset.computedRange?.toIso ?? toIso
  const granularity = useGranularityState({ fromIso: effectiveFrom, toIso: effectiveTo })
  const governance = resolveGovernance(effectiveFrom, effectiveTo, granularity.unit)

  // ── Row 15-light: Vorperioden-Basis (pre-window baseline panel) ───────────────
  // Applicable only when the window actually partitions into ≥2 panels and the
  // multi-timeframe picker is empty (panels there are per-TF, not sub-period).
  const [panelBase, setPanelBase] = usePanelBaseState()
  const windowBuckets = countBuckets(effectiveFrom, effectiveTo, governance.effectiveUnit)
  const multiTfActive =
    archetype === 'performance' && multiTimeframe && multiTimeframeState.entries.length > 0
  const panelBaseApplicable =
    panelBase === 'prev' && granularity.dots < windowBuckets /* ≥2 panels */ && !multiTfActive
  const panelBaseline = panelBaseApplicable
    ? {
        baselineFromIso: shiftBackByBuckets(
          effectiveFrom,
          governance.effectiveUnit,
          granularity.dots,
        ),
      }
    : null

  // ── Arrival reconcile: inapplicable dims + foreign kpi_root + stale campaign, ONE URL write ──
  // Instead of wiping ALL dims on a fact switch, we drop ONLY dims the active fact's catalog lacks
  // (applicable dims survive). FU-31 folds in the foreign-kpi_root reset; Campaigns C1 (spec §4)
  // folds in the campaign identity-collapse clear. All four are INDEPENDENT legs of ONE effect:
  // each has its own actionability test, and when two or more settle in the same commit their keys
  // UNION into a single setMany call.
  //
  // This single-writer discipline is LOAD-BEARING, not cosmetic (review 2026-07-09, amended FU-31
  // 2026-07-14): setMany's functional updater closes over the render's searchParams (react-router
  // updates its ref post-commit), so two same-commit writes both build on the same base and the LAST
  // navigate wins wholesale — a separate writer would RESURRECT the keys an earlier write deleted
  // (a standalone campaign clear would restore a just-reset kpi_root). Any future arrival-time URL
  // reconcile MUST become a leg here, never a second effect. { replace: true } so cross-page arrival
  // REPLACES (no Back-button trap); each leg fires on first settle too (a targeted strip only removes
  // inapplicable state, so the prev===null guard is gone).
  //
  // Companion hazard: new arrival-time URL writers must be legs here (per the doctrine above), while
  // mount-time layout-effect URL writers elsewhere have a dropped-write hazard documented at
  // useRouteStateHygiene.ts (see its header comment).
  //
  // The union dep array re-fires the effect for ANY leg's trigger. The kpi and campaign legs
  // self-neutralize after they act (foreignRoot→null once the root re-derives; campaignParam→''
  // once cleared), but the dims leg does NOT: under an active campaign a carried MANAGED dim on an
  // incompatible fact stays in `dropped` by design (it's exempt from deletion), so `dropped` is
  // persistently non-empty. So the dims leg alone is gated on an actual droppedKey CHANGE (its own
  // trigger) via lastHandledDroppedKeyRef — otherwise a sibling-leg edit (a filter/date/preset
  // change) would resurrect a dismissed prune notice and re-write groupby on every keystroke.
  const [prunedNotice, setPrunedNotice] = useState<string[] | null>(null)
  const [removedKpiRoot, setRemovedKpiRoot] = useState<string | null>(null)
  const foreignRoot = kpiCascade.foreignRoot
  const controlsSettled = controls.data !== undefined
  const setMany = useRef(setMultipleSearchParams)
  setMany.current = setMultipleSearchParams
  const droppedKey = pruned.dropped.join('|') // stable string dep — effect fires iff the dropped set changes
  const lastHandledDroppedKeyRef = useRef<string | null>(null) // null → first arrival fires
  // §1 leg 4 (Bug-1b, 2026-07-31): pin-knob keys are foreign on Review routes. Stripped in
  // THIS writer per the single-writer doctrine above. Stable string dep like droppedKey;
  // self-neutralizes after the write (set becomes empty).
  const pinKnobKey = [...searchParams.keys()].filter(isPinKnobKey).join('|')
  useEffect(() => {
    const dimsActionable =
      pruneReady && pruned.dropped.length > 0 && droppedKey !== lastHandledDroppedKeyRef.current
    // Loose `!= null` so a cascade mock that omits foreignRoot (undefined) never reads as actionable.
    const kpiActionable = foreignRoot != null
    // Campaigns C1 (spec §4): identity collapses when the campaign param no longer resolves, or the
    // RAW URL state (filters/preset/dates) diverges from the resolved campaign's predicate. Compare
    // RAW state — never the prune/preset-derived effective values, which would false-collapse a
    // consistent campaign (tf_preset==='custom' ⇒ computedRange null, so raw === effective anyway).
    const campaignActionable =
      campaignParam !== '' &&
      controlsSettled &&
      (campaign === null ||
        !campaignConsistent(campaign.item, filters, timeframePreset.preset, fromIso, toIso))
    const pinKnobsActionable = pinKnobKey !== ''
    if (!dimsActionable && !kpiActionable && !campaignActionable && !pinKnobsActionable) return
    const next: Record<string, string | null> = {}
    if (dimsActionable) {
      // Explicit groupby (overrides a page seed, per useGroupByState), delete each dropped
      // dim.<x>, null `columns` if pruned.
      next['groupby'] = pruned.groupBy.join(',')
      const campaignActive = campaignParam !== ''
      for (const dim of pruned.dropped) {
        // Campaigns C1 (spec §4): while a campaign is active its managed dims stay in the URL
        // even when the active fact lacks them — the request-level prune (effectiveFilters)
        // still excludes them; keeping the URL materialized is what makes chip-absence always
        // mean user intent and lets the predicate reappear on facts that support it.
        if (campaignActive && (CAMPAIGN_MANAGED_DIMS as readonly string[]).includes(dim)) continue
        next[`dim.${dim}`] = null
      }
      if (rawColumns && pruned.columns === null) next['columns'] = null
    }
    if (kpiActionable) {
      // FU-31: deleting the three cascade params re-derives the page default naturally
      // (effectiveRoot = root || seeded.root || firstRoot) — no hardcoded reset value.
      next['kpi_root'] = null
      next['kpi_driver'] = null
      next['kpi_value_type'] = null
    }
    // Campaigns C1: clearing writes ONLY the campaign key — the user's edited params stand.
    if (campaignActionable) next['campaign'] = null
    // Leg 4: delete every carried pin-knob key (Bug-1b §1 — Review does not host pin state).
    // A4 (07-31 ledger): keys are RE-DERIVED from searchParams inside the closure —
    // pinKnobKey stays ONLY the change-detection dep. Reconstructing keys by splitting the
    // joined dep on '|' shreds any pin whose walk_call JSON itself contains a pipe.
    if (pinKnobsActionable)
      for (const k of [...searchParams.keys()].filter(isPinKnobKey)) next[k] = null
    setMany.current(next, { replace: true })
    // The notice still receives ALL dropped dims (managed included) — visibility is the point.
    if (dimsActionable) {
      setPrunedNotice(pruned.dropped)
      lastHandledDroppedKeyRef.current = droppedKey // this dropped set is handled; don't re-fire on sibling edits
    }
    if (kpiActionable) setRemovedKpiRoot(foreignRoot)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pruneReady,
    droppedKey,
    pinKnobKey,
    foreignRoot,
    campaignParam,
    controlsSettled,
    campaign,
    filters,
    timeframePreset.preset,
    fromIso,
    toIso,
  ])
  const dismissPrunedNotice = () => setPrunedNotice(null)
  const dismissRemovedKpiNotice = () => setRemovedKpiRoot(null)

  // Seed is_default filter chips from the REGISTRY-resolved active fact, never the static prop
  // (they differ on ReviewKpis). Pass null until the registry settles so the seed can't fire
  // against the transient static fallback (useDimDefaults treats null as "not ready" → no query).
  // Currently a no-op: 0 is_default rows in v_value_labels. Per-active-fact re-seeding on a later
  // fact switch stays with the fact-switch task (useDimDefaults' guard is once-per-mount).
  useDimDefaults(registrySettled ? activeFactTable : null)

  // Campaigns C3 (spec D-C3-1): a campaign arm REPLACES all campaign-owned dims (exact + managed)
  // with its fact-aware resolvedFilters — {Campaign:[id]} on carrying facts, catalog-pruned
  // attribution filters otherwise. mergeArmFilters is a no-op when campaign is null/non-campaign
  // (empty resolvedFilters), so effectiveFilters flows through unchanged off the campaign path.
  const baseParams: WalkBaseParams = {
    fact_table: activeFactTable,
    kpi: selectedKpi,
    from: effectiveFrom,
    to: effectiveTo,
    filter_dims: campaign
      ? mergeArmFilters(effectiveFilters, campaign.resolvedFilters)
      : effectiveFilters,
    group_by: effectiveGroupBy,
    bucket_size: toWireBucket(governance.effectiveUnit),
  }

  // Final-review Minor: labelForRootFromCache linear-scans every cached get_controls query — memoize
  // on removedKpiRoot so it doesn't re-run on every unrelated render while the FU-31 notice is up.
  const removedKpi = useMemo(
    () => (removedKpiRoot ? labelForRootFromCache(qc, removedKpiRoot) : null),
    [removedKpiRoot, qc],
  )

  return {
    pageId,
    factTable: activeFactTable,
    archetype,
    fromIso: effectiveFrom,
    toIso: effectiveTo,
    kpi: selectedKpi,
    setKpi: (k: string) => kpiCascade.setRoot(k),
    filters: effectiveFilters,
    groupBy: effectiveGroupBy,
    decompFacet: decomp?.facet ?? null,
    groupByDefault: decomp?.groupByDefault ?? '',
    availableKpis,
    baseParams,
    setMultipleSearchParams,
    kpiCascade,
    granularity,
    governance,
    panelBase,
    setPanelBase,
    windowBuckets,
    panelBaseline,
    timeframePreset,
    timeframeReady,
    multiTimeframe: archetype === 'performance' && multiTimeframe ? multiTimeframeState : null,
    prunedDims: prunedNotice,
    dismissPrunedNotice,
    removedKpi,
    dismissRemovedKpiNotice,
    dimCatalogError: dimCatalog.isError,
    campaign,
    ratioComponents,
    ratioComponentControlsError,
    ratioMapPending,
  }
}
